/**
 * 見出しの下の 1 行「今いちばん大事なこと」（ADR-0055）。DOM を持たない部分。
 *
 * 正は 1 か所（原則 12）: いずれ Gateway が Jarvis の判断として `focus` を返す（GET /v1/briefing に足す予定の欄）。
 * それが届けばそのまま出す。届かない間（今の Gateway）は、朝のまとめの「日付のある件」（期日のあるリストの項目・覚えている日付の事実）
 * のうち、いちばん近いものを出す。近さは Gateway が決めた並び（position。days_until の昇順）に従い、ここでは選び直さない。
 * 何も無ければ null（行を出さない）。
 */
import type { BriefingItemView, BriefingView } from "./briefing-model.js";

export interface Focus {
  /** 本文（Gateway がコードで作った定型文、または Jarvis の判断の文） */
  text: string;
  /** 「ほか N 件」。無ければ 0 */
  more: number;
  /** どこから来たか（計測・表示の印） */
  source: "gateway" | "briefing";
}

/** Gateway が将来返す形（任意）。文だけが必須 */
export interface FocusFromGateway {
  text: string;
  more?: number;
}

/** 朝のまとめのうち「日付のある件」。Gateway 側の group 0 と同じ種類（packages/memory の briefing） */
const DATED: ReadonlySet<BriefingItemView["source_kind"]> = new Set(["list_due", "fact_date"]);

export function datedItems(items: readonly BriefingItemView[]): BriefingItemView[] {
  return items
    .filter((i) => i.status === "shown" && DATED.has(i.source_kind))
    .sort((a, b) => a.position - b.position);
}

export function focusOf(
  view: (BriefingView & { focus?: FocusFromGateway | null }) | null | undefined,
): Focus | null {
  if (!view) return null;
  const g = view.focus;
  if (g && typeof g.text === "string" && g.text.trim())
    return { text: g.text.trim(), more: Math.max(0, Math.floor(g.more ?? 0)), source: "gateway" };
  if (!view.enabled || view.frozen) return null;
  const dated = datedItems(view.items);
  const first = dated[0];
  if (!first) return null;
  return { text: first.text, more: dated.length - 1, source: "briefing" };
}
